import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter, MapPin, Search, X } from "lucide-react";

import { useInventory, useLocationTree } from "@/api/queries";
import type { InventoryRow, LocationNode } from "@/api/types";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { Input, Select } from "@/components/Input";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { stockLabel, stockTone } from "@/lib/stock";
import { PageHeader } from "@/layout/PageHeader";
import { Page } from "@/layout/Page";
import { formatQuantity, formatRelative, formatUnit } from "@/lib/format";

const PAGE_SIZE = 50;

/**
 * The inventory table (TICKET FE-006).
 *
 * A professional table, not an AI experience. Every figure — available, reserved,
 * physical — comes from the server; the frontend formats, it never computes.
 */
export function InventoryPage() {
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(term);

  const locationId = params.get("location") ?? undefined;
  const inStockOnly = params.get("stock") === "1";
  const offset = Number(params.get("offset") ?? 0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [term]);

  const query = useMemo(
    () => ({
      q: debounced || undefined,
      location_id: locationId,
      in_stock_only: inStockOnly,
      limit: PAGE_SIZE,
      offset,
    }),
    [debounced, locationId, inStockOnly, offset],
  );

  const { data, isLoading, error, refetch } = useInventory(query);
  const { data: locations } = useLocationTree();

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  const flatLocations = useMemo(() => flatten(locations ?? []), [locations]);
  const hasFilters = Boolean(debounced || locationId || inStockOnly);

  return (
    <Page>
      <PageHeader
        title="Inventaire"
        subtitle="Ce qui est physiquement là, ce qui est réservé, et ce qui est réellement disponible."
      />

      {/* The filter bar sits outside the scrolling area so it stays put while
          the rows scroll under it. */}
      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                update({ q: event.target.value, offset: undefined });
              }}
              placeholder="Nom, fabricant, MPN, SKU ou code-barres…"
              className="pl-9"
              aria-label="Rechercher dans l'inventaire"
            />
          </div>

          <Select
            value={locationId ?? ""}
            onChange={(event) => update({ location: event.target.value || undefined, offset: undefined })}
            aria-label="Filtrer par emplacement"
            className="sm:w-56"
          >
            <option value="">Tous les emplacements</option>
            {flatLocations.map((location) => (
              <option key={location.id} value={location.id}>
                {" ".repeat(location.depth * 2)}
                {location.name} ({location.code})
              </option>
            ))}
          </Select>

          <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-border bg-surface-sunken px-3 text-[13px] text-ink-muted">
            <input
              type="checkbox"
              checked={inStockOnly}
              onChange={(event) => update({ stock: event.target.checked ? "1" : undefined, offset: undefined })}
              className="accent-[#f2661f]"
            />
            En stock
          </label>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setTerm("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
        {isLoading && <TableSkeleton rows={12} columns={8} />}
        {error && <ErrorState error={error} onRetry={() => void refetch()} />}

        {data && data.items.length === 0 && (
          <EmptyState
            icon={<Filter className="size-6" aria-hidden />}
            title={hasFilters ? "Aucun article ne correspond" : "L'inventaire est vide"}
            description={
              hasFilters
                ? "Essayez un autre terme, ou retirez les filtres."
                : "Ajoutez un produit au catalogue, puis recevez du stock pour le voir apparaître ici."
            }
          />
        )}

        {data && data.items.length > 0 && (
          <>
            {/* Desktop: the table. */}
            <div className="hidden md:block">
              <TableShell>
                <thead>
                  <tr>
                    <Th className="w-16">Photo</Th>
                    {/* max-w-0 is the classic auto-layout table trick: it lets
                        the cell take its share of the width instead of being
                        pushed out by its own text, so `truncate` inside it
                        actually engages. Without it a long product name makes
                        the table wider than its container and the operator has
                        to scroll sideways — which no column threshold can fix,
                        because the width depends on the customer's data. */}
                    <Th className="max-w-0">Produit</Th>
                    {/* The reveal points below are measured, not guessed. Forcing the
                  table to its min-content width and hiding columns one at a
                  time gives the container width each set actually needs:

                     5 cols (base)          ~ 530 px
                     7 cols (+ réservé/phys) ~ 720 px   -> @3xl  (768)
                     9 cols (+ fabricant/MPN) ~1020 px  -> @5xl  (1024)
                    10 cols (+ catégorie)    ~1130 px   -> @6xl  (1152)
                    11 cols (+ dernier mvt)  ~1290 px   -> @min-[82rem] (1312)

                  The last one is why this file changed: 11 columns were being
                  shown from 1024 px of container, which needs 1292. They fitted
                  by overflowing — the operator had to scroll the table sideways
                  to reach Statut and Emplacement, on a 1440 px screen. */}
              <Th className="hidden @5xl:table-cell">Fabricant</Th>
                    <Th className="hidden whitespace-nowrap @5xl:table-cell">MPN</Th>
                    <Th className="hidden @6xl:table-cell">Catégorie</Th>
                    <Th align="right">Disponible</Th>
                    <Th align="right" className="hidden @3xl:table-cell">Réservé</Th>
                    <Th align="right" className="hidden @3xl:table-cell">Physique</Th>
                    <Th className="max-w-0">Emplacement principal</Th>
                    <Th>Statut</Th>
                    <Th className="hidden whitespace-nowrap @min-[82rem]:table-cell">Dernier mouvement</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <InventoryTableRow key={row.product_id} row={row} />
                  ))}
                </tbody>
              </TableShell>
            </div>

            {/* Mobile: compact cards, not a squeezed table. */}
            <ul className="divide-y divide-border/60 md:hidden">
              {data.items.map((row) => (
                <li key={row.product_id}>
                  <InventoryCard row={row} />
                </li>
              ))}
            </ul>
          </>
        )}
        </div>

        {data && (
          <Pagination
            offset={data.offset}
            limit={data.limit}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>
    </Page>
  );
}

function InventoryTableRow({ row }: { row: InventoryRow }) {
  return (
    <Tr>
      <Td>
        <Photo mediaId={row.media_id} alt={row.name} className="size-10" />
      </Td>
      <Td className="max-w-0">
        <Link
          to={`/products/${row.product_id}`}
          className="block truncate font-medium text-ink hover:text-brand"
        >
          {row.name}
        </Link>
        {row.sku && (
          <p className="truncate font-mono text-[11px] text-ink-subtle">{row.sku}</p>
        )}
      </Td>
      <Td className="hidden text-ink-muted @5xl:table-cell">{row.manufacturer ?? "—"}</Td>
      <Td className="hidden whitespace-nowrap font-mono text-[12px] text-ink-muted @5xl:table-cell">
        {row.part_number ?? "—"}
      </Td>
      <Td className="hidden text-ink-muted @6xl:table-cell">{row.category_name ?? "—"}</Td>
      <Td align="right" className="tnum font-semibold">
        {formatQuantity(row.available_quantity)}
        <span className="ml-1 text-[11px] font-normal text-ink-subtle">{formatUnit(row.unit)}</span>
      </Td>
      <Td align="right" className="tnum hidden text-ink-muted @3xl:table-cell">
        {formatQuantity(row.reserved_quantity)}
      </Td>
      <Td align="right" className="tnum hidden text-ink-muted @3xl:table-cell">
        {formatQuantity(row.book_quantity)}
      </Td>
      <Td className="max-w-0">
        {row.primary_place_label ? (
          <span className="flex items-center gap-1.5 text-[13px] text-ink-muted">
            <MapPin className="size-3.5 shrink-0 text-ink-subtle" aria-hidden />
            <span className="truncate">{row.primary_place_label}</span>
            {row.location_count > 1 && (
              <span className="shrink-0 rounded border border-border px-1 text-[10px] text-ink-subtle">
                +{row.location_count - 1}
              </span>
            )}
          </span>
        ) : (
          <span className="text-ink-subtle">—</span>
        )}
      </Td>
      <Td>
        <StatusBadge tone={stockTone(row.available_quantity, row.book_quantity)}>
          {stockLabel(row.available_quantity, row.book_quantity)}
        </StatusBadge>
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-subtle @min-[82rem]:table-cell">
        {formatRelative(row.last_movement_at)}
      </Td>
    </Tr>
  );
}

function InventoryCard({ row }: { row: InventoryRow }) {
  return (
    <Link to={`/products/${row.product_id}`} className="flex items-center gap-3 px-3 py-3 active:bg-surface-hover">
      <Photo mediaId={row.media_id} alt={row.name} className="size-14 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{row.name}</p>
        <p className="truncate text-[12px] text-ink-subtle">
          {[row.manufacturer, row.part_number].filter(Boolean).join(" · ") || "—"}
        </p>
        <p className="mt-1 flex items-center gap-1 text-[12px] text-ink-muted">
          <MapPin className="size-3 shrink-0" aria-hidden />
          <span className="truncate">{row.primary_place_label ?? "Aucun emplacement"}</span>
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="tnum text-lg font-semibold leading-none text-ink">
          {formatQuantity(row.available_quantity)}
        </p>
        <p className="text-[11px] text-ink-subtle">{formatUnit(row.unit)} dispo.</p>
        <StatusBadge
          tone={stockTone(row.available_quantity, row.book_quantity)}
          className="mt-1"
        >
          {stockLabel(row.available_quantity, row.book_quantity)}
        </StatusBadge>
      </div>
    </Link>
  );
}

interface FlatLocation {
  id: string;
  code: string;
  name: string;
  depth: number;
}

function flatten(nodes: LocationNode[], depth = 0): FlatLocation[] {
  return nodes.flatMap((node) => [
    { id: node.id, code: node.code, name: node.name, depth },
    ...flatten(node.children, depth + 1),
  ]);
}
